import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
    type UpdateAudiCalculationModuleMutation,
    type UpdateAudiCalculationModuleMutationVariables,
    UpdateAudiCalculationModuleDocument,
} from '../../../../api/mutations/updateAudiCalculationModule';
import { type AudiCalculationModuleUpdateSettings } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

type AudiCalculationModuleUpdateFormProps = {
    moduleId: string;
    initialValues: AudiCalculationModuleUpdateSettings;
    onComplete: () => void;
};

const loadingMessageKey = 'primary';

const AudiCalculationModuleUpdateForm = ({
    moduleId,
    initialValues,
    onComplete,
}: AudiCalculationModuleUpdateFormProps) => {
    const { t } = useTranslation(['audiCalculationModuleDetails', 'moduleDetails']);

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError(
        async (values: AudiCalculationModuleUpdateSettings) => {
            if (!values.displayName) {
                return;
            }

            message.loading({
                content: t('audiCalculationModuleDetails:messages.updateMainDetailsSubmitting'),
                key: loadingMessageKey,
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateAudiCalculationModuleMutation, UpdateAudiCalculationModuleMutationVariables>({
                    mutation: UpdateAudiCalculationModuleDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy(loadingMessageKey);
                });

            message.success({
                content: t('audiCalculationModuleDetails:messages.updateMainDetailsSuccessful'),
                key: 'primary',
            });

            // close the drawer
            onComplete();
        },
        [moduleId, apolloClient, onComplete, t]
    );

    const validate = useValidator(validators.requiredNonEmptyString('displayName'));

    return (
        <Formik<AudiCalculationModuleUpdateSettings>
            initialValues={initialValues}
            onSubmit={onSubmit}
            validate={validate}
        >
            {({ handleSubmit }) => (
                <Form
                    id="updateAudiCalculationModule"
                    layout="vertical"
                    name="updateAudiCalculationModule"
                    onSubmitCapture={handleSubmit}
                >
                    <InputField
                        {...t('moduleDetails:fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                </Form>
            )}
        </Formik>
    );
};

export default AudiCalculationModuleUpdateForm;
